import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';
import debouce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import { TextInput, V2SearchIcon } from '@paypalcorp/pp-react';

import {
  updateSuggestions,
  fullSearchFired,
  autoSuggestionClicked,
  clearQualtricsIntercepts,
} from '../actions/action-auto-complete';
import SpeechRecognition from './SpeechRecognition';
import { getElmoExperiments } from '../../home/actions/action-app';
import {
  showTopReason,
  getTopSearchReason,
  hideTopReason,
  onFocusOfInputBox,
} from '../../home/actions/actions-top-contact-reason';
import {
  startRecording,
  endRecording,
  speechRecognitionRequest,
  resetSpeechQuery,
} from '../actions/action-speech-recognition';
import { searchType, clickAnalytics, clickParser } from '../../lib/insightHelper';

import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { elmoExperimentResourceNames, elmoTreatmentNames } from '../../lib/experiments-helper';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import getHistory from '../../lib/HistoryHelper';
import { ARTICLE_CONTENT } from '../constants/PropTypesShapes';
import { getBNATitle } from '../../lib/messageHelper';
import { isBotExperienceEnabled, isShowOnlySearchLayoutEnabled } from '../../../experimentations/helper';
import { ECM_KEY_DOCID, ECM_DOC_ID_DEFAULT_VALUE } from '../constants/ecmFPTITagsKeys';

const searchPlaceHolder = {
  largeScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.largeScreen'),
  smallScreen: i18n.message('smarthelp/heroSection.searchbox.placeholderText.smallScreen'),
  hcrPlaceHolder: i18n.message('smarthelp/heroSection.searchbox.placeholderText.hcrPlaceHolder'),
  speechHolder: i18n.message('smarthelp/heroSection.searchbox.speechRecognition.placeholder'),
};

const viewSearchResult = i18n.message('smarthelp/heroSection.searchbox.viewSearchResult');
const defaultTopReason = i18n.message('smarthelp/heroSection.searchbox.defaultTopReasonforHome');

const errorTextMessage = {
  error: i18n.message('smarthelp/heroSection.searchbox.maxCharError'),
};

const cobrand = Page.getCountry().toLowerCase();
const defaultSearchTreatment = 'Trmt_GOPS_Helpcenter_PCC_Default_Search_1';
const searchResult = { searchResultText: '' };
export class SearchBox extends Component {
  // Autosuggest propTypes expects this function to be present
  static getSearchIcon() {
    return 'vx_icon vx_icon-small vx_icon-magnifying-glass';
  }

  static getSanitizedValue(value) {
    searchResult.searchResultText = value;
    return value ? value.trim() : '';
  }

  static getPlaceHolderText() {
    if (Page.isMobileLayout()) {
      return searchPlaceHolder.smallScreen;
    }
    return searchPlaceHolder.hcrPlaceHolder;
  }

  // render HCR suggestions followed by View all Results
  static renderSuggestion(faq) {
    if (faq.title === viewSearchResult) {
      return <div className="viewSearchResult">{viewSearchResult}</div>;
    }
    const text = faq.title ? SearchBox.highlightSearch(faq.title) : '';
    return <div className="result-item" dangerouslySetInnerHTML={{ __html: text }} />;
  }

  static highlightSearch(data) {
    const list = data.toLowerCase();
    if (searchResult && searchResult.searchResultText) {
      const key = searchResult.searchResultText.toLowerCase();

      try {
        const pattern = new RegExp(key, 'gi');
        const replaceWith = `${data
          .substr(list.search(searchResult.searchResultText.toLowerCase()), searchResult.searchResultText.length)
          .bold()}`;
        const filteredText = data.replace(pattern, replaceWith);
        return filteredText;
      } catch (e) {
        return data;
      }
    }
    return data;
  }

  static addViewResultIntoSuggestion(suggestions) {
    const submitSearchResult = {
      title: viewSearchResult,
    };
    if (suggestions.length > 0 && suggestions.every((suggestion) => suggestion.title !== viewSearchResult)) {
      suggestions.push(submitSearchResult);
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      inputText: '',
      inputFocus: false,
      enterHit: false,
    };
    this.MIN_LENGTH = 1; // min characters required for search
    this.suggestSelected = false; // preventing auto-complete request after selected one of the suggestions

    this.submitForm = this.submitForm.bind(this);
    this.debouncedUpdateSuggestions = debouce(this.props.updateSuggestions, 300);

    // action-auto-complete functions
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);

    // view all result getting the state inputText
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleClearKeyPress = this.handleClearKeyPress.bind(this);
    this.renderClearButton = this.renderClearButton.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.redirectSearchPage = this.redirectSearchPage.bind(this);
    this.startSpeechRecoginition = this.startSpeechRecoginition.bind(this);
    this.endSpeechRecoginition = this.endSpeechRecoginition.bind(this);
    this.speechRecoginitionErrorHandler = this.speechRecoginitionErrorHandler.bind(this);
    this.renderTopReason = this.renderTopReason.bind(this);
    this.getSuggestionHeader = this.getSuggestionHeader.bind(this);
  }

  // Check for the elmo treatment of maxlength and get the max length from RC
  async componentDidMount() {
    // API call
    await this.props.getElmoExperiments(elmoExperimentResourceNames.searchLimiterResourceName);
    const maxLengthChars = Page.searchInputMaxString();
    if (this.props.treatments.indexOf(elmoTreatmentNames.searchLimiterTreatmentName) >= 0 && maxLengthChars) {
      this.setState({
        maxLength: maxLengthChars,
        maxCharError: false,
        currentChar: 0,
        maxLengthElmoTreatment: true,
      });
    } else {
      this.setState({
        maxLength: 250,
        maxCharError: false,
        currentChar: 0,
        maxLengthElmoTreatment: false,
      });
    }
    // clear the  autosuggestion-clicked fullsearch-clicked if the search bar is loaded in home page
    if (Page.isHomePage()) {
      this.props.clearQualtricsIntercepts();
    }
    this.props.getTopSearchReason();
  }

  componentWillReceiveProps(props) {
    if (props.q !== this.props.q) {
      this.setState({
        inputText: props.q,
        currentChar: props.q.length,
      });
    }
  }

  handleClearKeyPress(event) {
    if (event.key === 'Enter') {
      this.clearSearch();
    }
  }

  onChange(event, { newValue }) {
    if (event.type === 'change') {
      this.setState({
        inputText: newValue,
        enterHit: false,
        currentChar: newValue.length,
      });
    }
    if (newValue.length === this.state.maxLength) {
      trackPage({
        id: `searchCharLimiterTriggered${Page.getFPTIPageId()}`,
        link: 'smarthelp-search-char-limit-error',
        srch: `${this.state.inputText}`,
      });
      this.setState({
        maxCharError: true,
      });
    } else {
      this.setState({
        maxCharError: false,
      });
    }

    if (newValue.length > 0 || this.props.q) {
      // Default contact reasons handler
      this.props.hideTopReason();
    }
  }

  onFocus() {
    this.setState({
      inputFocus: true,
    });

    // FPTI: click on the search box
    const fptiPageSearchBarFocused = `searchBarFocusedIn${Page.getFPTIPageName()}`;
    trackLink({ id: fptiPageSearchBarFocused, link: 'smarthelp-hcr-search-bar-focused' });

    this.props.onFocusOfInputBox(true);
    // Default contact reasons handler
    if (this.isEnableDefaultReason()) {
      this.props.showTopReason();

      // FPTI: quicklinks for common questions shown activity
      const fptiPageSearchBarQuicklinks = `quicklinksShownIn${Page.getFPTIPageName()}`;
      trackPage({ id: fptiPageSearchBarQuicklinks, link: 'smarthelp-hcr-common-questions-quicklinks-shown' });
    }
  }

  onBlur() {
    this.setState({
      inputFocus: false,
    });
    this.props.onFocusOfInputBox(false);
    // Default contact reasons handler
    if (this.isEnableDefaultReason() || this.props.q) {
      this.props.hideTopReason();
    }
  }

  onKeyUp(event) {
    if (event.key === 'Enter') {
      event.target.blur();
      this.setState({
        enterHit: true,
      });
    }
  }

  onSuggestionsFetchRequested({ value }) {
    // HACK: Cancel fetching the auto-complete event after selecting one of the suggestions
    if (this.suggestSelected) {
      this.suggestSelected = false;
      return null;
    }

    const sanitizedValue = Page.getSanitizedValue(value);
    const sanitizedInputText = Page.getSanitizedValue(this.state.inputText);
    // dont show the result when max char is reached
    if (
      sanitizedInputText !== sanitizedValue &&
      sanitizedValue.length > this.MIN_LENGTH &&
      sanitizedValue.length < this.state.maxLength
    ) {
      this.debouncedUpdateSuggestions(sanitizedValue);
    }
    return null;
  }

  onSuggestionSelected(event, { suggestion }) {
    event.preventDefault();
    event.stopPropagation();
    // articles clicked from auto search. Useful for qulatrics intercepts
    this.props.autoSuggestionClicked();
    this.suggestSelected = true; // Mark one suggestion be selected

    if (suggestion.title === viewSearchResult) {
      trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search-term', srch: `${this.state.inputText}` });
      this.goToSearchResult();
    } else {
      const title = Page.getFaqUrlFromTitle(suggestion.title);
      const faq = suggestion.docid ? suggestion.docid.toLowerCase() : [];
      // get the ECM API tags from article data
      const fptiDocId = suggestion[ECM_KEY_DOCID] || ECM_DOC_ID_DEFAULT_VALUE;

      if (faq.length > 0) {
        // Top contact reasons will be displayed when this.props.isDisplayTopReason is true
        if (this.props.isDisplayTopReason) {
          trackLink({
            id: `${this.props.FPTIId}`,
            link: `faq-autoquicklink|${faq}`,
            hpid: faq,
            help_doc_id: fptiDocId,
          });
        } else {
          trackLink({
            id: `${this.props.FPTIId}`,
            link: `smarthelp-selected-suggested-keyword-km-auto-complete|${this.state.inputText}|${title}|${suggestion.docid}|${suggestion.rank}|total-${this.props.searchResults.length}`,
            hpid: faq,
            help_doc_id: fptiDocId,
          });
        }
        window.location = `/${cobrand}/smarthelp/article/${title}-${faq}?app=searchAutoComplete`;
      }

      const rank = this.getSuggestionRank(suggestion);
      if (rank) {
        clickAnalytics(
          clickParser({
            csrf: Page.getCSRFtoken(),
            articleId: suggestion.docid,
            articleTitle: suggestion.title,
            rank,
            query: this.state.inputText,
            type: searchType.autoComplete,
            a_track: suggestion.a_track,
          }),
        );
      }
    }
  }

  getSuggestionRank(suggestion) {
    if (!suggestion || !suggestion.docid || !Array.isArray(this.props.searchResults)) {
      return null;
    }

    // Find suggestion rank based on the search result; p.s. rank starts from 1
    for (let i = 0; i < this.props.searchResults.length; i += 1) {
      if (this.props.searchResults[i].docid === suggestion.docid) {
        return i + 1;
      }
    }

    return 0;
  }

  getSuggestionValue(faq) {
    if (faq.title === viewSearchResult) {
      return this.state.inputText;
    }
    return faq.title;
  }

  // formate contact top reason data which is same with spotlight search
  getDefaultSeachReason() {
    if (!this.props.isDisplayTopReason) {
      return [];
    }

    if (this.isEnablePCCDefaultArticles()) {
      return this.props.defaultArticles.slice(0, 5);
    }
    return this.props.defaultTopReason;
  }

  // Get the default auto-complete header as the first option
  getSuggestionHeader() {
    if (this.isEnablePCCDefaultArticles()) {
      return getBNATitle();
    }

    return defaultTopReason;
  }

  // Adding the suggestion header in the suggestions array
  addDefaultReason(suggestions, isValid) {
    const defaultReasonList = {
      title: this.getSuggestionHeader(),
    };
    const defaultReason = isValid ? defaultReasonList : {};
    const tempArray = [...suggestions];
    if (
      !_isEmpty(defaultReason) &&
      suggestions.length &&
      suggestions.every((suggestion) => suggestion.title !== defaultTopReason)
    ) {
      tempArray.unshift(defaultReason);
      return tempArray;
    }
    return tempArray;
  }

  isEnablePCCDefaultArticles() {
    return (
      Array.isArray(this.props.treatments) &&
      this.props.treatments.indexOf(defaultSearchTreatment) >= 0 &&
      Array.isArray(this.props.defaultArticles) &&
      this.props.defaultArticles.length > 0
    );
  }

  isEnableDefaultReason() {
    return (
      !this.hasMinimimSearchCharacters() &&
      Array.isArray(this.props.defaultTopReason) &&
      this.props.defaultTopReason.length > 0
    );
  }

  hasMinimimSearchCharacters() {
    const sanitizedInputText = SearchBox.getSanitizedValue(this.state.inputText);
    return sanitizedInputText.length >= this.MIN_LENGTH;
  }

  goToSearchResult() {
    const q = Page.getSanitizedValue(this.state.inputText);
    const { channel } = this.props.queryParams;
    const channelQueryParam = channel ? `&channel=${channel}` : '';
    const searchUrl = `search?q=${q}${channelQueryParam}`;
    this.props.resetSpeechQuery();

    // Redirect to Search Page, if its from Channel Steering.
    if (Page.getPageName() === 'contact-us') {
      const searchBaseUrl = Page.getBaseURLWithExperience();
      window.location = `/${searchBaseUrl}/${searchUrl}`;
    } else {
      const history = getHistory();
      history.push(`/${searchUrl}`);
    }
  }

  submitForm(event) {
    event.preventDefault();
    if (this.state.inputText) {
      // Empty search text will not fire FPTI request
      trackLink({
        id: `fullSearchClickedIn${Page.getFPTIPageName()}`,
        link: 'smarthelp-full-search-clicked',
        srch: `${this.state.inputText}`,
      });
    }

    const searchText = Page.getSanitizedValue(this.state.inputText);
    if (searchText.length < this.MIN_LENGTH) {
      return;
    }
    this.props.fullSearchFired();
    this.goToSearchResult();
  }

  clearSearch() {
    // FPTI event for clearing search
    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-hcr-clear-search' });
    this.props.hideTopReason();
    this.setState({
      inputText: '',
      maxCharError: false,
    });
  }

  // Start event hander for SpeechRecognition
  startSpeechRecoginition() {
    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search|speech-to-text|start' });
    this.props.startRecording();
  }

  endSpeechRecoginition() {
    this.props.endRecording();
  }

  speechRecoginitionErrorHandler(error) {
    trackLink({ id: `${this.props.FPTIId}`, link: `smarthelp-search|speech-to-text|error|${error}` });
  }

  // Onresult event handler for the SpeechRecognition component
  redirectSearchPage(input) {
    const query = Page.getSanitizedValue(input);
    this.props.speechRecognitionRequest(query);
    const { channel } = this.props.queryParams;
    const channelQueryParam = channel ? `&channel=${channel}` : '';

    trackLink({ id: `${this.props.FPTIId}`, link: 'smarthelp-search|speech-to-text|search', srch: `${input}|speech` });
    const history = getHistory();
    history.push(`/search?q=${query}${channelQueryParam}`);
  }

  renderTopReason(faq) {
    if (faq.title === this.getSuggestionHeader()) {
      return <div className="defaultTopReason">{this.getSuggestionHeader()}</div>;
    }
    if (faq.title) {
      return <div className="default-result-item">{faq.title}</div>;
    }
    return '';
  }

  // Rendering SpeechRecognition when ELMO is enable and browser type is Chrome
  renderSpeechRecoginition() {
    if (!Page.isShowSpeechRecognition()) {
      return null;
    }

    return (
      <SpeechRecognition
        onStart={this.startSpeechRecoginition}
        onEnd={this.endSpeechRecoginition}
        onResult={this.redirectSearchPage}
        onError={this.speechRecoginitionErrorHandler}
      />
    );
  }

  renderClearButton() {
    if (!this.state.inputText.trim()) return null;

    return (
      <div // eslint-disable-line jsx-a11y/no-static-element-interactions
        className="cw_icon cw_icon-small cw_icon-close-small clear-btn"
        tabIndex="0"
        role="button"
        aria-label="clear search"
        onKeyPress={this.handleClearKeyPress}
        onClick={this.clearSearch}
      />
    );
  }

  renderCharCount() {
    if (this.state.inputText.length > 0 && this.state.maxLengthElmoTreatment) {
      return (
        <span className={this.state.maxCharError ? 'characterCountError' : 'characterCount'}>
          {`${this.state.currentChar}/${this.state.maxLength}`}
        </span>
      );
    }
    return '';
  }

  renderAutoSuggetion({ suggestions, renderInput }) {
    const attr = {
      suggestions,
      onSuggestionsFetchRequested: this.onSuggestionsFetchRequested,
      onSuggestionsClearRequested: SearchBox.onSuggestionsClearRequested,
      onSuggestionSelected: this.onSuggestionSelected,
      getSuggestionValue: this.getSuggestionValue,
      renderSuggestion: SearchBox.renderSuggestion,
      focusInputOnSuggestionClick: false,
      inputProps: this.inputProps,
      renderInputComponent: renderInput,
      alwaysRenderSuggestions: true,
    };

    if (this.props.isDisplayTopReason) {
      attr.renderSuggestion = this.renderTopReason;
    }
    return <Autosuggest {...attr} />;
  }

  render() {
    this.inputProps = {
      label: SearchBox.getPlaceHolderText(),
      value: this.state.inputText || '',
      onChange: this.onChange,
      onBlur: this.onBlur,
      onFocus: this.onFocus,
      onKeyUp: this.onKeyUp,
      className: 'search-input',
      maxLength: this.state.maxLength,
      id: 'help-input-text',
      leftIcon: (
        <button type="submit" className="search-btn" aria-label="Search" onClick={this.submitForm}>
          <V2SearchIcon size="sm" />
        </button>
      ),
      rightIcon: (
        <div>
          {this.renderCharCount()}
          {this.renderClearButton()}
          {this.renderSpeechRecoginition()}
        </div>
      ),
    };

    // Update Search box placeholder when recording users' speech
    if (this.props.isSpeechRecording) {
      this.inputProps.placeholder = searchPlaceHolder.speechHolder;
    }
    if (this.state.maxCharError && this.state.maxLengthElmoTreatment) {
      this.inputProps.name = 'error';
      this.inputProps.errorText = errorTextMessage.error;
    }

    const containerStyle = classNames({
      'input-text-container': true,
      'input-text__speech-recognition-enable': Page.isShowSpeechRecognition(),
    });

    const renderInput = (inputProps) => (
      <div className={containerStyle}>
        <TextInput {...inputProps} />
      </div>
    );
    let suggestions = [];
    // Innovation Guild: Search only Experiment in Help Center, disable auto suggestion
    if (isBotExperienceEnabled() || isShowOnlySearchLayoutEnabled()) {
      suggestions = [];
    } else if (
      this.hasMinimimSearchCharacters() &&
      this.state.inputFocus &&
      !this.state.enterHit &&
      !this.state.maxCharError
    ) {
      suggestions = this.props.searchResults;
      suggestions = this.addDefaultReason(suggestions, true);
      SearchBox.addViewResultIntoSuggestion(suggestions);
    } else {
      suggestions = this.getDefaultSeachReason();
      suggestions = this.addDefaultReason(suggestions, false);
    }

    return (
      <div className="search-box">
        <form role="search">
          <div className="search-box-contents">{this.renderAutoSuggetion({ suggestions, renderInput })}</div>
        </form>
      </div>
    );
  }
}

const suggestionItem = PropTypes.shape({
  title: PropTypes.string,
  docid: PropTypes.string,
});

SearchBox.propTypes = {
  isSpeechRecording: PropTypes.bool,
  q: PropTypes.string.isRequired,
  queryParams: PropTypes.shape({
    channel: PropTypes.string,
  }),
  isDisplayTopReason: PropTypes.bool,
  updateSuggestions: PropTypes.func.isRequired,
  getTopSearchReason: PropTypes.func.isRequired,
  hideTopReason: PropTypes.func.isRequired,
  showTopReason: PropTypes.func.isRequired,
  startRecording: PropTypes.func.isRequired,
  endRecording: PropTypes.func.isRequired,
  resetSpeechQuery: PropTypes.func.isRequired,
  speechRecognitionRequest: PropTypes.func.isRequired,
  onFocusOfInputBox: PropTypes.func.isRequired,
  defaultTopReason: PropTypes.arrayOf(suggestionItem).isRequired,
  searchResults: PropTypes.arrayOf(suggestionItem).isRequired,
  FPTIId: PropTypes.string,
  treatments: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultArticles: PropTypes.arrayOf(PropTypes.shape(ARTICLE_CONTENT)).isRequired,
  getElmoExperiments: PropTypes.func.isRequired,
  fullSearchFired: PropTypes.func.isRequired,
  autoSuggestionClicked: PropTypes.func.isRequired,
  clearQualtricsIntercepts: PropTypes.func.isRequired,
};

SearchBox.defaultProps = {
  q: '',
  isSpeechRecording: false,
  queryParams: {},
  FPTIId: 'homePage',
  isDisplayTopReason: null,
  treatments: [],
  defaultArticles: [],
};

function mapStateToProps(state) {
  return {
    isSpeechRecording: state.speechRecognition.isRecording,
    q: state.searchResult.q,
    queryParams: {
      channel: state.query.params.channel,
    },
    searchResults: state.autoComplete.searchResults,
    isDisplayTopReason: state.contactReason.isDisplayTopReason,
    defaultTopReason: state.contactReason.defaultTopReason,
    treatments: state.app.elmoTreatments,
    defaultArticles: state.defaultPCCArticles.defaultArticles, // default PCC articles
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      updateSuggestions,
      showTopReason,
      getTopSearchReason,
      hideTopReason,
      startRecording,
      endRecording,
      speechRecognitionRequest,
      resetSpeechQuery,
      onFocusOfInputBox,
      getElmoExperiments,
      fullSearchFired,
      autoSuggestionClicked,
      clearQualtricsIntercepts,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchBox);
